/**
 * 统一数据表格组件
 *
 * @description 提供统一的表格样式和交互，确保所有列表页面的一致性
 * @version 1.0.0
 * @author AI Assistant
 * @date 2025-09-23
 *
 * @example
 * <UnifiedDataTable
 *   columns={columns}
 *   dataSource={data}
 *   loading={loading}
 *   pagination={{ pageSize: 10 }}
 * />
 *
 * @see docs/design/UI_CONSISTENCY_FRAMEWORK.md
 */

import React from 'react';
import { Table, TableProps, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import './UnifiedDataTable.css';

// 统一表格列配置接口
export interface UnifiedTableColumn<T = any> {
  title: string;
  dataIndex?: string | string[];
  key: string;
  width?: number;
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, record: T, index: number) => React.ReactNode;
  sorter?: boolean | ((a: T, b: T) => number);
  filters?: Array<{ text: string; value: any }>;
  onFilter?: (value: any, record: T) => boolean;
  fixed?: 'left' | 'right';
  ellipsis?: boolean | { showTitle?: boolean };
  defaultSortOrder?: 'ascend' | 'descend';
  className?: string;
}

// 统一表格Props接口
export interface UnifiedDataTableProps<T = any>
  extends Omit<TableProps<T>, 'columns'> {
  columns: UnifiedTableColumn<T>[];
  dataSource: T[];
  loading?: boolean;
  className?: string;
  testId?: string;
  // 扩展配置
  showHeader?: boolean;
  bordered?: boolean;
  size?: 'small' | 'middle' | 'large';
  tableLayout?: 'auto' | 'fixed';
}

/**
 * 统一数据表格组件
 */
export const UnifiedDataTable = <T extends Record<string, any>>({
  columns,
  dataSource,
  loading = false,
  className = '',
  testId = 'unified-data-table',
  showHeader = true,
  bordered = false,
  size = 'middle',
  tableLayout = 'fixed',
  pagination,
  scroll = { x: 'max-content' },
  locale,
  ...restProps
}: UnifiedDataTableProps<T>) => {
  const { t } = useTranslation();

  // 处理列配置，确保符合Ant Design Table的格式
  const processedColumns: ColumnsType<T> = columns.map(column => ({
    ...column,
  }));

  const defaultPagination = React.useMemo(
    () => ({
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total: number, range: [number, number]) =>
        t('common:pagination.range', {
          start: range[0],
          end: range[1],
          total,
        }),
      pageSize: 10,
      pageSizeOptions: ['10', '20', '50', '100'],
    }),
    [t]
  );

  const mergedPagination = React.useMemo(() => {
    if (pagination === false) {
      return false;
    }
    if (pagination && typeof pagination === 'object') {
      return { ...defaultPagination, ...pagination };
    }
    return defaultPagination;
  }, [pagination, defaultPagination]);

  // 默认空状态配置
  const defaultLocale = React.useMemo(
    () => ({
      emptyText: (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('common:message.noData')}
          style={{ padding: '40px 0' }}
        />
      ),
    }),
    [t]
  );

  return (
    <div
      className={`unified-table-container ${className}`}
      data-testid={testId}
    >
      <Table<T>
        columns={processedColumns}
        dataSource={dataSource}
        loading={loading}
        showHeader={showHeader}
        bordered={bordered}
        size={size}
        tableLayout={tableLayout}
        pagination={mergedPagination}
        scroll={scroll}
        locale={locale || defaultLocale}
        className="unified-data-table"
        rowClassName={() => 'unified-table-row'}
        {...restProps}
      />
    </div>
  );
};

export default UnifiedDataTable;
